// Drugs › Compare (ba-web): every drug the Drug list offers against every target it is modelled on, as one table of
// predicted occupancies, read from the runs' own headers (`predictions[].occupancy` at each scenario's exposure, the
// equilibrium model O = C / (C + Ki) the headers name). A PK course without predictions (LSD's dose-to-cortex course)
// contributes the peak of its own occupancy series instead, and the table says so. The gallery's "Where 12 drugs meet
// the cortex" is twelve cortex maps; this is the numbers behind them, with each drug's map one click away.

import { create } from 'zustand';
import { useAtlas } from '../app/store.ts';
import { defaultSource, SimFiles } from '../data/sims/files.ts';
import { PharmaSim, pkCourse } from '../data/sims/pharma.ts';
import type { PharmaHeader } from '../data/sims/types.ts';
import measured from '../data/pet-measured.json';

/** Hansen 2022's PET atlas, the 19 targets in its column order (sims/pharma/atlas/atlas.json `target_codes`): the
 *  columns' order, and the count the footer compares against. */
export const ATLAS_TARGETS = ['5HT1a', '5HT1b', '5HT2a', '5HT4', '5HT6', '5HTT', 'A4B2', 'CB1', 'D1', 'D2', 'DAT', 'GABAa', 'H3', 'M1', 'mGluR5', 'MOR', 'NET', 'NMDA', 'VAChT'];

export interface CompareCell {
  target: string;
  /** Predicted occupancy, 0–1, with the header's low–high spread where it has one. */
  central: number;
  low: number | null;
  high: number | null;
  /** Ki, nM, where the header records it. */
  kiNm: number | null;
  /** Where the number comes from: the header's prediction at the scenario's exposure, or a course's peak. */
  from: 'prediction' | 'course-peak';
}

export interface CompareRow {
  /** The Drug list's value (the run's path) and label. */
  value: string;
  label: string;
  /** The run's own name and scenario (dose and route). */
  name: string;
  scenario: string | null;
  primary: string | null;
  /** Free brain concentration the predictions are at, nM (central), where the header records it. */
  exposureNm: number | null;
  cells: CompareCell[];
  /** Why a row has no numbers (no binding modelled: the header's `excluded` reasons). */
  note: string | null;
}

export interface CompareTable {
  rows: CompareRow[];
  /** The targets any row names (modelled or primary), in the atlas's order; unknown codes after. */
  targets: string[];
}

/** One row from a run's header, and its course's peak occupancy where the header predicts none. */
export function compareRow(value: string, label: string, h: PharmaHeader, peak?: { targets: string[]; central: Float32Array; low: Float32Array | null; high: Float32Array | null; frames: number } | null): CompareRow {
  const cells: CompareCell[] = [];
  for (const p of h.predictions ?? []) {
    const c = p.occupancy?.central;
    if (typeof c !== 'number' || !Number.isFinite(c)) continue;
    cells.push({ target: p.target, central: c, low: num(p.occupancy?.low), high: num(p.occupancy?.high), kiNm: num(p.affinity?.central_nm), from: 'prediction' });
  }
  if (!cells.length && peak && peak.central.length) {
    const T = peak.targets.length;
    peak.targets.forEach((t, k) => {
      let best = -1;
      let at = -1;
      for (let f = 0; f < peak.frames; f++) {
        const v = peak.central[f * T + k]!;
        if (Number.isFinite(v) && v > best) {
          best = v;
          at = f;
        }
      }
      if (at < 0) return;
      cells.push({ target: t, central: best, low: num(peak.low?.[at * T + k]), high: num(peak.high?.[at * T + k]), kiNm: null, from: 'course-peak' });
    });
  }
  const excluded = (h.excluded ?? []).filter((e) => e.reason);
  const note = cells.length ? null : excluded.length ? `No binding modelled: ${[...new Set(excluded.map((e) => e.reason!))].join('; ')} (${excluded.map((e) => e.target).join(', ')}).` : 'No binding modelled.';
  return {
    value,
    label,
    name: h.name ?? h.drug ?? h.id,
    scenario: h.scenario?.label ?? null,
    primary: h.primary_target ?? null,
    exposureNm: num(h.exposure_nm?.central),
    cells: cells.sort((a, b) => order(a.target) - order(b.target)),
    note,
  };
}

/** The rows' targets in the atlas's order: every target a row is modelled on or names as primary. */
export function compareTable(rows: CompareRow[]): CompareTable {
  const named = new Set<string>();
  for (const r of rows) {
    for (const c of r.cells) named.add(c.target);
    if (r.primary) named.add(r.primary);
  }
  return { rows, targets: [...named].sort((a, b) => order(a) - order(b) || a.localeCompare(b)) };
}

/** A cell's number as the table writes it: whole per cent, "<1 %" under one. */
export const occupancyText = (v: number): string => (v < 0.005 ? '<1 %' : `${Math.round(v * 100)} %`);

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const order = (t: string): number => {
  const i = ATLAS_TARGETS.indexOf(t);
  return i < 0 ? ATLAS_TARGETS.length : i;
};

/** A measured human PET study beside a prediction (`src/data/pet-measured.json`, written by `scripts/pet-measured.py` from the
 *  pharma lane's `pet_validation.json` and its frozen forward comparisons). Numbers are the source's own: an observed range is the
 *  min and max of the rows' reported values, never an average across studies. */
export interface MeasuredStudy {
  id: string;
  citation: string;
  url: string;
  tracer: string;
  region: string;
  timing: string;
  n_subjects: number;
  rows: number;
  doses: string[];
  /** The doses as one phrase: '1, 2, 5 mg/day haloperidol'. */
  doses_text: string;
  observed_range_percent: [number, number] | null;
  observed_sd_percent: [number, number] | null;
  comparison_mode: string;
  /** What the rows are: paired plasma and occupancy, or a dose-only / cohort-range reference constraint. */
  mode_text: string;
  reference_only: boolean;
  notes: string | null;
  /** The registered research note that scored the study, and its result in a line. */
  registered: { path: string; result: string };
}

export interface MeasuredEntry {
  drug: string;
  target: string;
  observed_range_percent: [number, number] | null;
  studies: MeasuredStudy[];
}

// The JSON's ranges are typed as number[] by the compiler; the generator writes pairs or null.
const MEASURED_FILE = measured as unknown as { entries: MeasuredEntry[]; independence_policy: string };
export const MEASURED: MeasuredEntry[] = MEASURED_FILE.entries;
export const INDEPENDENCE_POLICY: string = MEASURED_FILE.independence_policy;

/** The measured PET entry for a Drug-list run (`pharma/<drug>`) and a target, or null. The LSD course (`lsd-200ug-cortex-v1`) is a
 *  different run id from any study's drug, so it carries none. */
export function measuredFor(value: string, target: string): MeasuredEntry | null {
  const drug = value.replace(/^pharma\//, '');
  return MEASURED.find((e) => e.drug === drug && e.target === target) ?? null;
}

const pct1 = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));
/** A measured range as the token writes it: "53–88", "1.8–72.4". */
export const rangeText = (r: [number, number] | null): string => (r ? (r[0] === r[1] ? pct1(r[0]) : `${pct1(r[0])}–${pct1(r[1])}`) : '');

/** The short author–year of a citation: "Kapur et al. (1997), …" → "Kapur 1997". */
export const studyName = (citation: string): string => {
  const m = /^([A-Z][\w’'-]+)(?: et al\.)? \((\d{4})\)/.exec(citation);
  return m ? `${m[1]} ${m[2]}` : citation.split(',')[0]!;
};

/** One study's line for the hover: what was measured, in whom, at what dose and when; what kind of comparison its rows allow;
 *  and the registered note that scored it. */
export function measuredStudyText(s: MeasuredStudy): string {
  const doses = s.doses_text;
  const sd = s.observed_sd_percent ? ` (SD ${rangeText(s.observed_sd_percent)})` : '';
  const who = `${s.n_subjects} ${s.n_subjects === 1 ? 'person' : 'people'}, ${s.rows} ${s.rows === 1 ? 'row' : 'rows'}`;
  return `◇ ${studyName(s.citation)}, ${s.tracer}, ${s.region}, ${who}: ${rangeText(s.observed_range_percent)} % occupied${sd} at ${doses}; ${s.timing} · ${s.mode_text} · registered in ${s.registered.path}: ${s.registered.result}`;
}

/** Every study of an entry, one line each. */
export const measuredText = (e: MeasuredEntry): string[] => e.studies.map(measuredStudyText);

export type CompareStatus = 'idle' | 'loading' | 'ready' | 'error';

interface CompareState {
  open: boolean;
  status: CompareStatus;
  /** The Drug list the table was read for (its values joined), so a new list reads again. */
  key: string | null;
  table: CompareTable | null;
  why: string | null;
  setOpen(open: boolean): void;
}

export const useCompare = create<CompareState>((set) => ({
  open: false,
  status: 'idle',
  key: null,
  table: null,
  why: null,
  setOpen: (open) => set({ open }),
}));

const reason = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Read the table for the Drug list's available options (once per list): each run's header, and the series of a
 *  course without predictions. */
export async function openCompare(options: { value: string; label: string }[]): Promise<void> {
  const key = options.map((o) => o.value).join(' ');
  const st = useCompare.getState();
  if (st.key === key && (st.status === 'ready' || st.status === 'loading')) return;
  useCompare.setState({ status: 'loading', key, why: null });
  try {
    const read = await Promise.all(
      options.map(async (o): Promise<CompareRow | null> => {
        const files = new SimFiles(defaultSource(), o.value);
        const h = await files.json<PharmaHeader>('header.json');
        // The Drug list also offers the receptor atlas itself (kind 'comparison', continuous receptor landscapes): not a drug.
        if (h.kind === 'comparison' || (!h.drug && !h.primary_target && !(h.predictions ?? []).length)) return null;
        let peak: Parameters<typeof compareRow>[3] = null;
        if (!(h.predictions ?? []).some((p) => typeof p.occupancy?.central === 'number') && h.files?.series?.length) {
          const sim = await PharmaSim.open(files, h);
          const course = pkCourse(await sim.series());
          if (course.occupancy.central) peak = { ...course.occupancy, central: course.occupancy.central, frames: sim.axis.frames };
        }
        return compareRow(o.value, o.label, h, peak);
      }),
    );
    const rows = read.filter((r): r is CompareRow => r !== null);
    if (useCompare.getState().key !== key) return;
    useCompare.setState({ status: 'ready', table: compareTable(rows), why: null });
  } catch (e) {
    if (useCompare.getState().key !== key) return;
    useCompare.setState({ status: 'error', table: null, why: reason(e) });
  }
}

// Leaving Drugs closes the sheet (a module-level subscription: an effect cleanup would run once at mount under StrictMode).
useAtlas.subscribe(
  (s) => s.scenario,
  (scenario) => {
    if (scenario !== 'drugs' && useCompare.getState().open) useCompare.getState().setOpen(false);
  },
);

/** The Drug list as the sheet reads it: the scene's available options for `drug`. */
export const drugOptions = (): { value: string; label: string }[] => {
  const s = useAtlas.getState();
  const opts = s.sims.scenario === s.scenario ? s.sims.options.drug : undefined;
  return (opts ?? []).filter((o) => o.available).map((o) => ({ value: o.value, label: o.label }));
};
